import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Queue } from '@sentry/indexeddb-client';
import { concat, fromEvent, interval, merge, Observable, of } from 'rxjs';
import {
  first,
  map,
  switchMap,
  tap,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs/operators';
import { DeferredRequest, RequestDbItem } from './offline-client';

const checkForApplicationUpdatesHours = 4;

export const networkDatabaseName = 'network-status';
export const networkObjectStoreName = 'requests';

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  /**
   * Observable that emits the current offline status.
   *
   * iOS 16.0-16.2 WebKit bug: navigator.onLine incorrectly reports false
   * (offline) even when online, particularly on iPhone 14 series devices.
   * The bug causes rapid false offline/online events.
   *
   * Solution: debounceTime(100) filters out rapid transitions to prevent
   * the offline banner from flickering. This is non-breaking as:
   * - Real network changes are still detected within 100ms
   * - Prevents false positives from briefly showing offline banner
   * - Works normally on all other browsers/devices
   */
  readonly isOffline$ = merge(
    of(null),
    fromEvent(window, 'online'),
    fromEvent(window, 'offline')
  ).pipe(
    debounceTime(100), // Debounce rapid false triggers from iOS 16.x WebKit bug
    map(() => !navigator.onLine),
    distinctUntilChanged()
  );

  constructor(
    private readonly httpClient: HttpClient,
    private readonly appRef: ApplicationRef,
    private readonly swUpdate: SwUpdate
  ) {}

  handleOffline() {
    return this.isOffline$.pipe(
      switchMap((isOffline) => {
        if (isOffline) {
          return of(undefined);
        }

        const client = new Queue<RequestDbItem>({
          databaseName: networkDatabaseName,
          objectStoreName: networkObjectStoreName,
        });

        const handleRequest = (): Observable<RequestDbItem | null> => {
          return client.dequeue().pipe(
            switchMap((r) => {
              if (!r) {
                return of(null);
              }

              const request: DeferredRequest = JSON.parse(r.request);

              if (request.type === 'POST') {
                return this.httpClient
                  .post(request.url, request.body)
                  .pipe(switchMap(() => handleRequest()));
              }

              if (request.type === 'PUT') {
                return this.httpClient
                  .put(request.url, request.body)
                  .pipe(switchMap(() => handleRequest()));
              }
              throw new Error('Deferred http request not parseable');
            })
          );
        };

        return handleRequest();
      })
    );
  }

  pollForApplicationUpdates() {
    const appIsStable$ = this.appRef.isStable.pipe(
      first((isStable) => isStable === true)
    );
    const updateInterval$ = interval(
      checkForApplicationUpdatesHours * 60 * 60 * 1000
    );
    return concat(appIsStable$, updateInterval$).pipe(
      tap(() => {
        if (this.swUpdate.isEnabled) {
          this.swUpdate.checkForUpdate();
        }
      })
    );
  }
}
